import {
  OPTIONS_AIDE_MATERIELLE,
  OPTIONS_REGIME_BIEN,
  OPTIONS_TYPE_CONVENTION_FICHE,
  type CasCode,
} from '@/constants/casConventionCodes';
import DEFAULT_PACS from '@/constants/defaultPACS';
import OPTIONS_TYPE_CONVENTION from '@/constants/optionsTypeConvention';
import OPTIONS_TYPE_COMPARUTION from '@/constants/optionsTypeComparution';
import STATUT_PACS from '@/constants/StatutPACS';
import resolveCasCode from './resolveCasCode';
import resolveSexe from './resolveSexe';
import sexeToSexeCode from './sexeToSexeCode';
import {
  estTypeAdresseCommune,
  extractLibelleVoie,
  stripTypeVoie,
  toNumeroVoie,
  toTypeAdresseLabel,
} from './formatConventionAdresse';
import { decomposerNaissance } from './dateNaissance';


// Récupère le premier prénom et les prénoms complémentaires
const splitPrenom = (prenom: string | null, prenomsComplementaires: string | null) => {
  if (!prenom) return { prenom: null, prenomsComplementaires: prenomsComplementaires ?? null };
  const commaIndex = prenom.indexOf(',');
  if (commaIndex === -1) {
    return { prenom: prenom.trim(), prenomsComplementaires: prenomsComplementaires ?? null };
  }
  return {
    prenom: prenom.substring(0, commaIndex).trim(),
    prenomsComplementaires: prenom.substring(commaIndex + 1).trim() || null,
  };
};

// Résout un libellé texte (ex: "En cours") vers le key numérique de l'option
// correspondante dans une liste { key, text }. Tolère les variations mineures
// (accents, casse, fautes de frappe) via un score de mots communs.
const resolveLabelToKey = (
  label: string | null,
  options: Array<{ key: number | string; text: string | number }>,
): number | null => {
  if (!label) return null;
  const normalize = (s: string) =>
    s
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]/g, ' ')
      .split(/\s+/)
      .filter((w) => w.length > 3);

  const labelWords = new Set(normalize(label));
  let bestKey: number | null = null;
  let bestScore = 0;

  for (const opt of options) {
    const optWords = normalize(String(opt.text));
    const score = optWords.filter((w) => labelWords.has(w)).length;
    if (score > bestScore) {
      bestScore = score;
      bestKey = Number(opt.key);
    }
  }

  return bestScore > 0 ? bestKey : null;
};

// Champ codé de convention : le GET renvoie le libellé exact du backend
// (cf. casConventionCodes). `0` (« Non renseigné ») est ramené à null pour
// qu'aucun bouton radio de l'assistant ne soit coché.
const resolveCas = (cas: CasCode[], label: unknown): number | null =>
  resolveCasCode(cas, label) || null;

const resolveTypeConvention = (label: string | null): number | null =>
  resolveCas(OPTIONS_TYPE_CONVENTION_FICHE, label) ??
  resolveLabelToKey(label, OPTIONS_TYPE_CONVENTION);

const resolveStatut = (label: string | null): number | null =>
  resolveLabelToKey(label, STATUT_PACS);

const resolveTypeComparution = (label: string | null): number | null =>
  resolveLabelToKey(label, OPTIONS_TYPE_COMPARUTION);

// Normalise une valeur texte : chaîne vide -> null pour éviter le _.isNil
// ne renvoie pas true
const nullifyEmpty = (value: string | null | undefined): string | null =>
  value === null || value === undefined || value === '' ? null : value;

const ouiNonToBoolean = (value: unknown): boolean | null => {
  if (value === 'Oui' || value === true || value === 1) return true;
  if (value === 'Non' || value === false || value === 0) return false;
  return null;
};

const isDrapeauActif = (value: unknown): boolean =>
  value === 1 || value === true || value === '1';

// Case « Commune déléguée » de l'assistant : cochée dès qu'un libellé est
// enregistré, le drapeau seul n'étant pas fiable (absent des individus
// renvoyés par l'API, souvent à 0 sur les données reprises d'ETCV).
const hasCommuneDelegue = (drapeau: unknown, libelle: unknown): boolean =>
  isDrapeauActif(drapeau) || nullifyEmpty(libelle as string) !== null;

// Adresse d'acte : l'API renvoie le libellé dans `communeDelegue`
// (aa_lib_com_deleg) et le drapeau dans `estCommuneDelegue`
// (aa_int_com_deleg), alors que l'assistant attend le drapeau dans
// `communeDelegue` et le libellé dans `libelleCommuneDelegue`.
const mapCommuneDelegue = (adresse: any) => {
  const libelle = nullifyEmpty(adresse?.communeDelegue);

  return {
    communeDelegue: hasCommuneDelegue(adresse?.estCommuneDelegue, libelle)
      ? 1
      : 0,
    libelleCommuneDelegue: libelle,
  };
};

const mapAdresseReferentiel = (adresse: any) => ({
  identifiantVoie: adresse?.identifiantVoie ?? null,
  identifiantTypeVoie: adresse?.identifiantTypeVoie ?? null,
  typeVoie: nullifyEmpty(adresse?.typeVoie),
  lieuDit: nullifyEmpty(adresse?.lieuDit),
  identifiantCommune: adresse?.identifiantCommune ?? null,
  identifiantCodePostal: adresse?.identifiantCodePostal ?? null,
  identifiantDepartement: adresse?.identifiantDepartement ?? null,
  libelleDepartement: nullifyEmpty(adresse?.libelleDepartement),
});

// Champs renvoyés par l'assistant : à recharger, sinon la reprise les
// enverrait vides et l'API les effacerait.
const mapComplementsAdresse = (adresse: any) => ({
  type: toTypeAdresseLabel(adresse?.typeAdresse),
  lettre: adresse?.lettre ?? null,
  complementDistribution: adresse?.complementDistribution ?? null,
  complementIdentification: adresse?.complementIdentification ?? null,
  complementGeographique: adresse?.complementGeographique ?? null,
});

// `0` = pas de numéro côté ETCV : renvoyé tel quel, l'API le recopiait en tête
// de la ligne adresse (« 0 83190 Ollioules »).
const mapNumeroVoie = (numeroVoie: unknown): string | null =>
  numeroVoie === null || numeroVoie === undefined
    ? null
    : toNumeroVoie(numeroVoie);

// Libellé de voie d'une adresse d'acte : l'API le renvoie dans `libelleVoie`.
// `adresse` (ligne de rue unique) n'est qu'un repli pour les conventions
// historiques qui ne renseignent pas les champs granulaires.
// Le type de voie du référentiel est retiré du libellé : l'assistant le garde
// à part (étiquette du champ Voie) et le réintègre à l'envoi
// (cf. composeLibellesVoieConvention). Hors type « Commune », il reste dans le
// libellé (cf. estTypeAdresseCommune).
const resolveLibelleVoie = (adresse: any): string | null =>
  nullifyEmpty(
    stripTypeVoie(
      nullifyEmpty(adresse?.libelleVoie) ??
        extractLibelleVoie(adresse?.adresse, adresse?.numeroVoie),
      estTypeAdresseCommune(toTypeAdresseLabel(adresse?.typeAdresse))
        ? adresse?.typeVoie
        : null,
    ),
  );

// Si le pays de naissance n'est pas France, on force le code/libellé sur la valeur conventionnelle
// "étranger" plutôt que de reprendre le département reçu.
const resolveDepartementNaissance = (
  pays: string | null,
  code: string | null,
  libelle: string | null,
): { codeDepartement: string | null; libelleDepartement: string | null } => {
  if (pays && pays.trim().toLowerCase() !== 'france') {
    return {
      codeDepartement: '99',
      libelleDepartement: 'Pays et territoire étranger',
    };
  }
  return { codeDepartement: code ?? null, libelleDepartement: libelle ?? null };
};

// Reconstitue l'information de double nom pour le composant EtatCivil, qui
// pilote la case « Double nom » et le champ « Deuxième nom » via doubleNom /
// nomPartieUn / nomPartieDeux. L'API renvoie doubleNom en 1/0 ; si les deux
// parties ne sont pas fournies, on découpe nomFamille sur le premier espace
// (même logique que handleDoubleNomChange d'EtatCivil).
const resolveDoubleNom = (
  p: any,
): { doubleNom: boolean; nomPartieUn: string | null; nomPartieDeux: string | null } => {
  const doubleNom = p.doubleNom === 1 || p.doubleNom === true;
  let nomPartieUn = p.nomPartieUn ?? null;
  let nomPartieDeux = p.nomPartieDeux ?? null;

  if (
    doubleNom &&
    (nomPartieUn === null || nomPartieDeux === null) &&
    typeof p.nomFamille === 'string' &&
    p.nomFamille.includes(' ')
  ) {
    const idx = p.nomFamille.indexOf(' ');
    nomPartieUn = p.nomFamille.slice(0, idx);
    nomPartieDeux = p.nomFamille.slice(idx + 1);
  }

  return { doubleNom, nomPartieUn, nomPartieDeux };
};

// Reshape un registre brut (convention.registre ou store zustand) vers la
// forme attendue par pacs.registre (DEFAULT_PACS.registre).
export const mapRegistre = (registre: any) => ({
  ...DEFAULT_PACS.registre,
  identifiant: registre?.identifiant ?? null,
  annee: {
    ...DEFAULT_PACS.registre.annee,
    identifiant: registre?.annee?.identifiant ?? null,
    annee: registre?.annee?.annee ?? null,
  },
  libelle: registre?.libelle ?? null,
  numero: registre?.numero ?? null,
  numeroOrdre: registre?.numeroOrdre ?? null,
  type: registre?.type ?? null,
  numerotationManuelle: registre?.numerotationManuelle ?? null,
});

const mapPartenaire = (p: any) => {
  if (!p) return null;
  // Précision relue (jour ou mois inconnus) : pas de 1er inventé
  const birth = decomposerNaissance(p.naissanceDate, p.naissanceTypeDate);
  const { prenom, prenomsComplementaires } = splitPrenom(p.prenom, p.prenomsComplementaires);
  const sexe = resolveSexe(p.sexe, p.sexeCode);
  return {
    identifiant: p.identifiant ?? null,
    sexe,
    sexeCode: sexeToSexeCode(sexe),
    nomUsage: p.nomUsage ?? null,
    nomFamille: p.nomFamille ?? null,
    ...resolveDoubleNom(p),
    prenom,
    prenomsComplementaires,
    premiereNationalite: nullifyEmpty(p.premiereNationalite),
    deuxiemeNationalite: nullifyEmpty(p.deuxiemeNationalite),
    troisiemeNationalite: nullifyEmpty(p.troisiemeNationalite),
    ...birth,
    naissanceLibelleVoie: null,
    naissanceLettre: null,
    adresseNaissance: {
      libelleCodePostal: p.naissanceCodePostal ?? null,
      libelleCommune: p.naissanceLocalite ?? null,
      libellePays: p.naissancePays ?? null,
      ...resolveDepartementNaissance(
        p.naissancePays,
        p.naissanceDepartementCode,
        p.naissanceDepartementLibelle,
      ),
      naissanceLibelleCommuneDelegue: p.naissanceLibelleCommuneDelegue ?? null,
      naissanceCommuneDelegue: hasCommuneDelegue(
        p.naissanceCommuneDelegue,
        p.naissanceLibelleCommuneDelegue,
      ),
    },
    // Numéros conservés bruts : la conversion pour `PhoneInput` n'est faite qu'à
    // l'affichage, pour ne pas réécrire un numéro que l'utilisateur n'a pas modifié.
    tel1: nullifyEmpty(p.telephone1),
    tel2: nullifyEmpty(p.telephone2),
    mail: p.courriel ?? null,
    // `adresseResidence` (objet imbriqué) est le modèle INTERNE de l'assistant ;
    // ne pas le confondre avec `residenceAdresse`, le champ texte plat de l'API.
    // La voie vient de `residenceLibelleVoie` : `residenceAdresse` contient la
    // ligne complète formatée (« 84 ALLEE DES LAURIERS ROSES\r\n83190 OLLIOULES »),
    // pas le seul libellé de voie.
    //
    // Champs API volontairement ignorés : `residenceLettre`
    // (rôle distinct de `residenceArticle` non identifié), et les identifiants
    // référentiels (`residenceIdVoie`, `residenceCodeVoie`,
    // `residenceIdCodePostal`, `residenceInseeCode`, `residenceIdCommune`,
    // `residenceIdDepartement`, `residencePaysCode`, `residenceIdPays`,
    // `residenceCommuneDelegueNouvelleCommune`) utiles uniquement en recherche
    // assistée, alors que ce formulaire est en saisie libre.
    adresseResidence: {
      type: toTypeAdresseLabel(p.residenceType),
      libelleCodePostal: p.residenceCodePostal ?? null,
      libelleCommune: p.residenceLocalite ?? null,
      libellePays: p.residencePays ?? null,
      libelleDepartement: p.residenceDepartementLibelle ?? null,
      codeDepartement: p.residenceDepartementCode ?? null,
      libelleVoie:
        nullifyEmpty(p.residenceLibelleVoie) ??
        extractLibelleVoie(p.residenceAdresse, p.residenceNumeroVoirie),
      // Pas de champ dédié côté API : l'indice de répétition (Bis/Ter…) ne
      // vit que dans la ligne `residenceAdresse`.
      indiceRepetition: null,
      complementDistribution: p.residenceComplementDistribution ?? null,
      // `0` = pas de numéro côté ETCV (anciens PACS) : l'assistant
      // l'afficherait tel quel dans le champ numéro.
      numeroVoirie: nullifyEmpty(toNumeroVoie(p.residenceNumeroVoirie)),
      complementNumeroVoirie: p.residenceComplementNumeroVoirie ?? null,
      complementIdentification: p.residenceComplementIdentification ?? null,
      complementGeographique: p.residenceComplementGeographique ?? null,
      residenceLibelleCommuneDelegue: p.residenceLibelleCommuneDelegue ?? null,
      residenceCommuneDelegue: hasCommuneDelegue(
        p.residenceCommuneDelegue,
        p.residenceLibelleCommuneDelegue,
      ),
      // Idem indice de répétition : pas de champ dédié côté API.
      typeVoie: null,
      article: p.residenceArticle ?? null,
    },
    protectionJuridique: ouiNonToBoolean(p.protectionJuridique),
    protectionOfpra: ouiNonToBoolean(p.protectionOfpra),
    identiteVerifiee: ouiNonToBoolean(p.identiteVerifiee),
  };
};

const mapParent = (p: any) => {
  if (!p) return null;
  // Précision relue (jour ou mois inconnus) : pas de 1er inventé
  const birth = decomposerNaissance(p.naissanceDate, p.naissanceTypeDate);
  const { prenom, prenomsComplementaires } = splitPrenom(p.prenom, p.prenomsComplementaires);
  const sexe = resolveSexe(p.sexe, p.sexeCode);
  return {
    identifiant: p.identifiant ?? null,
    sexe,
    sexeCode: sexeToSexeCode(sexe),
    nomUsage: p.nomUsage ?? null,
    nomFamille: p.nomFamille ?? null,
    ...resolveDoubleNom(p),
    prenom,
    prenomsComplementaires,
    ...birth,
    adresseNaissance: {
      libelleCodePostal: p.naissanceCodePostal ?? null,
      libelleCommune: p.naissanceLocalite ?? null,
      libellePays: p.naissancePays ?? null,
      ...resolveDepartementNaissance(
        p.naissancePays,
        p.naissanceDepartementCode,
        p.naissanceDepartementLibelle,
      ),
      naissanceCommuneDelegue: hasCommuneDelegue(
        p.naissanceCommuneDelegue,
        p.naissanceLibelleCommuneDelegue,
      ),
      naissanceLibelleCommuneDelegue: p.naissanceLibelleCommuneDelegue ?? null,
    },
  };
};

// `complementInsee` (état matrimonial INSEE, dates, nombre d'enfants...) est
// une entité rattachée au Pacs, pas à la convention : /conventions/{id} ne la
// renvoie pas, elle doit être récupérée séparément (cf. StepSelectionPACS)
// et fournie ici pour être injectée dans le formulaire de l'assistant.
const mapConventionToPacs = (convention: any, complementInsee?: any): any => {
  return {
    ...DEFAULT_PACS,
    identifiant: convention.identifiantPacs ?? null,
    identifiantAdresseCommune: convention.identifiantAdresseCommune ?? null,
    identifiantLieuConvention: convention.identifiantLieuConvention ?? null,
    identifiantLieuDeclaration: convention.identifiantLieuDeclaration ?? null,
    identifiantLieuDissolution: convention.identifiantLieuDissolution ?? null,
    identifiantLieuPacs: convention.identifiantLieuPacs ?? null,
    identifiantTribunal: convention.identifiantTribunal ?? null,
    statut: resolveStatut(convention.statut),
    identifiantEntite: convention.identifiantEntite ?? null,
    registre: mapRegistre(convention.registre),
    complementInsee: {
      ...DEFAULT_PACS.complementInsee,
      ...complementInsee,
      // Le back renvoie ces booléens en smallint 0/1 : on les normalise pour
      // que les radios Oui/Non de l'assistant (comparaison stricte === true
      // / === false) fonctionnent bien.
      p1DejaPacse: ouiNonToBoolean(complementInsee?.p1DejaPacse),
      p2DejaPacse: ouiNonToBoolean(complementInsee?.p2DejaPacse),
    },
    conventionDeclaration: {
      ...DEFAULT_PACS.conventionDeclaration,
      conforme: convention.conforme ?? null,
      numerise: convention.numerise ?? null,
      dateEnregistrement: convention.dateEnregistrement ?? null,
      dateDeclarationJointDi: convention.dateDeclarationJointDi ?? null,
      identifiantTypeDissolution: convention.identifiantTypeDissolution ?? null,
      dateIrrecevabilite: convention.dateIrrecevabilite ?? null,
      identifiantIrrecevabilite: convention.identifiantIrrecevabilite ?? null,
      // Le libellé texte reçu (ex: "Comparution personnelle...") est résolu
      // vers le key numérique de l'option correspondante. Si la convention ne
      // renseigne pas le type de comparution (ou si le libellé ne correspond
      // à aucune option), on conserve la valeur par défaut.
      typeComparution:
        resolveTypeComparution(convention.typeComparution) ??
        DEFAULT_PACS.conventionDeclaration.typeComparution,
      dateDeclaration: convention.dateDeclaration ?? null,
      attestationLienParente: convention.attestationLienParente ?? null,
      attestationAdresseCommune: convention.attestationAdresseCommune ?? null,
      typeConvention:
        resolveTypeConvention(convention.typeConvention) ??
        DEFAULT_PACS.conventionDeclaration.typeConvention,
      dateConvention: convention.dateConvention ?? null,
      aideMaterielle: resolveCas(
        OPTIONS_AIDE_MATERIELLE,
        convention.aideMaterielle,
      ),
      valeurAide: convention.valeurAide ?? null,
      regimeBien: resolveCas(OPTIONS_REGIME_BIEN, convention.regimeBien),
      signataireNomFamille: convention.signataireNomFamille ?? null,
      signatairePrenom: convention.signatairePrenom ?? null,
      signataireQualite: convention.signataireQualite ?? null,
      commentaire: convention.commentaire ?? null,
      partenaire1DateDeces: convention.partenaire1?.decesDate ?? null,
      partenaire2DateDeces: convention.partenaire2?.decesDate ?? null,
      partenaire1: mapPartenaire(convention.partenaire1),
      partenaire2: mapPartenaire(convention.partenaire2),
      perePartenaire1: mapParent(convention.perePartenaire1),
      merePartenaire1: mapParent(convention.merePartenaire1),
      perePartenaire2: mapParent(convention.perePartenaire2),
      merePartenaire2: mapParent(convention.merePartenaire2),
      adresseConvention: {
        ...DEFAULT_PACS.conventionDeclaration.adresseConvention,
        codePostal: convention.adresseConvention?.codePostal ?? null,
        libelleCommune: convention.adresseConvention?.localite ?? null,
        localite: convention.adresseConvention?.localite ?? null,
        libellePays: convention.adresseConvention?.libellePays ?? null,
        numeroVoie: mapNumeroVoie(convention.adresseConvention?.numeroVoie),
        complementNumeroVoie:
          convention.adresseConvention?.complementNumeroVoie ?? null,
        libelleVoie: resolveLibelleVoie(convention.adresseConvention),
        codeDepartement: convention.adresseConvention?.codeDepartement ?? null,
        ...mapCommuneDelegue(convention.adresseConvention),
        ...mapComplementsAdresse(convention.adresseConvention),
        ...mapAdresseReferentiel(convention.adresseConvention),
      },
      adresseDeclaration: {
        ...DEFAULT_PACS.conventionDeclaration.adresseDeclaration,
        codePostal: convention.adresseDeclaration?.codePostal ?? null,
        libelleCommune: convention.adresseDeclaration?.localite ?? null,
        localite: convention.adresseDeclaration?.localite ?? null,
        libellePays: convention.adresseDeclaration?.libellePays ?? null,
        numeroVoie: mapNumeroVoie(convention.adresseDeclaration?.numeroVoie),
        complementNumeroVoie:
          convention.adresseDeclaration?.complementNumeroVoie ?? null,
        libelleVoie: resolveLibelleVoie(convention.adresseDeclaration),
        codeDepartement: convention.adresseDeclaration?.codeDepartement ?? null,
        ...mapCommuneDelegue(convention.adresseDeclaration),
        ...mapComplementsAdresse(convention.adresseDeclaration),
        ...mapAdresseReferentiel(convention.adresseDeclaration),
      },
      // `articleLocalite`, `communeDelegue` et `libelleCommuneDelegue` étaient
      // forcés à null ici, ce qui détruisait les valeurs enregistrées avant
      // même que LieuSaisie — leur seul éditeur — ne les affiche.
      adresseDissolution: {
        ...DEFAULT_PACS.conventionDeclaration.adresseDissolution,
        codePostal: convention.adresseDissolution?.codePostal ?? null,
        localite: convention.adresseDissolution?.localite ?? null,
        articleLocalite: convention.adresseDissolution?.articleLocalite ?? null,
        ...mapCommuneDelegue(convention.adresseDissolution),
        codeDepartement: convention.adresseDissolution?.codeDepartement ?? null,
        ...mapAdresseReferentiel(convention.adresseDissolution),
      },
      adressePacs: {
        ...DEFAULT_PACS.conventionDeclaration.adressePacs,
        codePostal: convention.adressePacs?.codePostal ?? null,
        localite: convention.adressePacs?.localite ?? null,
        articleLocalite: convention.adressePacs?.articleLocalite ?? null,
        ...mapCommuneDelegue(convention.adressePacs),
        codeDepartement: convention.adressePacs?.codeDepartement ?? null,
        ...mapAdresseReferentiel(convention.adressePacs),
      },
      adresseCommune: {
        ...DEFAULT_PACS.conventionDeclaration.adresseCommune,
        codePostal: convention.adresseCommune?.codePostal ?? null,
        libelleCommune: convention.adresseCommune?.localite ?? null,
        localite: convention.adresseCommune?.localite ?? null,
        libellePays: convention.adresseCommune?.libellePays ?? null,
        numeroVoie: mapNumeroVoie(convention.adresseCommune?.numeroVoie),
        complementNumeroVoie:
          convention.adresseCommune?.complementNumeroVoie ?? null,
        libelleVoie: resolveLibelleVoie(convention.adresseCommune),
        codeDepartement: convention.adresseCommune?.codeDepartement ?? null,
        ...mapCommuneDelegue(convention.adresseCommune),
        ...mapComplementsAdresse(convention.adresseCommune),
        ...mapAdresseReferentiel(convention.adresseCommune),
      },
    },
  };
};

export default mapConventionToPacs;
